import { useState, useCallback, useRef } from 'react';
import { IVideoClip, VideoEditConfig, IEpisode } from '@packages/global';
import { FFmpegExporter, ExportProgress } from '../utils/ffmpegExporter';
import { ExportConfig } from '../components/SuperVideoEditor/VideoExportDialog';
import { toast } from 'react-hot-toast';

export function useFFmpegExport() {
  const [isExporting, setIsExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState<ExportProgress | null>(null);
  const exporterRef = useRef<FFmpegExporter | null>(null);

  // 预加载 FFmpeg（可选）
  const preloadFFmpeg = useCallback(async () => {
    try {
      const exporter = new FFmpegExporter();
      await exporter.load();
      console.log('✅ FFmpeg 预加载完成');
      return exporter;
    } catch (error) {
      console.warn('⚠️ FFmpeg 预加载失败:', error);
      return null;
    }
  }, []);

  const startExport = useCallback(async (
    videoClips: IVideoClip[],
    editConfig: VideoEditConfig,
    episode: IEpisode | null,
    config: ExportConfig
  ) => {
    if (isExporting) {
      toast.error('已有导出任务正在进行');
      return;
    }

    if (!videoClips.length) {
      toast.error('没有可导出的视频片段');
      return;
    }

    setIsExporting(true);
    setExportProgress({
      stage: 'loading',
      progress: 0,
      message: '正在初始化 FFmpeg...'
    });

    try {
      const exporter = new FFmpegExporter((progress) => {
        setExportProgress(progress);
      });
      exporterRef.current = exporter;

      await exporter.load();

      // 新的高性能导出流程
      const { videoBlob, subtitleContent } = await exporter.exportVideoWithSubtitles(
        videoClips,
        editConfig,
        episode?.bgmUrl
      );

      // 下载视频文件
      const videoUrl = URL.createObjectURL(videoBlob);
      const videoLink = document.createElement('a');
      videoLink.href = videoUrl;
      videoLink.download = `${episode?.title || '视频'}.${config.format}`;
      videoLink.click();
      URL.revokeObjectURL(videoUrl);

      // 下载字幕文件
      if (subtitleContent) {
        const subtitleBlob = new Blob([subtitleContent], { type: 'text/plain' });
        const subtitleUrl = URL.createObjectURL(subtitleBlob);
        const subtitleLink = document.createElement('a');
        subtitleLink.href = subtitleUrl;
        subtitleLink.download = `${episode?.title || '视频'}.srt`;
        subtitleLink.click();
        URL.revokeObjectURL(subtitleUrl);
      }

      toast.success('视频和字幕导出成功！');

      setTimeout(() => {
        setIsExporting(false);
        setExportProgress(null);
      }, 2000);
    } catch (error) {
      console.error('Export failed:', error);
      const errorMessage = error instanceof Error ? error.message : '导出失败';
      
      if (errorMessage.includes('CDN') || errorMessage.includes('加载失败')) {
        toast.error('网络连接问题，请检查网络后重试');
      } else {
        toast.error(errorMessage);
      }
      
      setIsExporting(false);
      setExportProgress(null);
    } finally {
      exporterRef.current = null;
    }
  }, [isExporting]);

  const cancelExport = useCallback(async () => {
    if (exporterRef.current) {
      try {
        await exporterRef.current.terminate();
        toast.success('导出已取消');
      } catch (error) {
        console.error('Failed to cancel export:', error);
      }
    }
    setIsExporting(false);
    setExportProgress(null);
    exporterRef.current = null;
  }, []);

  return {
    isExporting,
    exportProgress,
    startExport,
    cancelExport,
    preloadFFmpeg
  };
}
